import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useHasPermissionSync } from "@/hooks/usePermissions";

export interface StayPackPetOption {
  petId: string;
  petName: string;
  ownerName: string;
}

type OwnersWithPetsRow = {
  pet_id: string | null;
  pet_name: string | null;
  owner_name: string | null;
  pet_death_date: string | null;
};

/**
 * Pet picker for the pack roster. Goes through `get_owners_with_pets` because
 * that RPC is the `patients:view`-gated search path (see CLAUDE.md); querying
 * `pets` directly would lean on RLS alone.
 */
export function useStayPackPetSearch(searchTerm: string, enabled = true) {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { hasPermission: canViewPatients, isLoading: isLoadingPermission } = useHasPermissionSync(
    "patients",
    "view",
  );
  const trimmed = searchTerm.trim();

  return useQuery<StayPackPetOption[]>({
    queryKey: ["stay-pack-pet-search", customerId, trimmed],
    enabled: enabled && canViewPatients && !isLoadingPermission && !!customerId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!customerId) return [];
      const hasSearch = trimmed.length > 0;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: hasSearch ? trimmed : null,
        p_offset: 0,
        p_limit: hasSearch ? 50 : 25,
      });
      if (error) throw error;

      const rows = ((data as OwnersWithPetsRow[] | null) ?? []).filter(
        (row) => row.pet_id && row.pet_name && !row.pet_death_date,
      );
      const seen = new Set<string>();
      const options: StayPackPetOption[] = [];
      for (const row of rows) {
        if (seen.has(row.pet_id!)) continue;
        seen.add(row.pet_id!);
        options.push({
          petId: row.pet_id!,
          petName: row.pet_name!,
          ownerName: row.owner_name ?? "",
        });
      }
      return options;
    },
  });
}
